import React, { useState } from 'react';
import { format } from 'date-fns';

interface DailyReport {
  id: string;
  reportDate: string;
  status: 'GENERATING' | 'READY' | 'ERROR';
  summary?: string;
  keyFindings?: any;
  recommendations?: any;
  overallScore?: number;
  isRead: boolean;
  readAt?: string;
  agentReports?: AgentReport[];
  createdAt: string;
}

interface AgentReport {
  id: string;
  agentId: string;
  findings: string;
  insights?: any;
  agent: {
    id: string;
    name: string;
    role: string;
    avatar?: string;
  };
}

interface DailyReportCardProps {
  report: DailyReport;
  onMarkAsRead?: (reportId: string) => void;
}

export const DailyReportCard: React.FC<DailyReportCardProps> = ({ report, onMarkAsRead }) => {
  const [expanded, setExpanded] = useState(false);

  const getStatusBadge = () => {
    switch (report.status) {
      case 'READY':
        return (
          <span className="px-3 py-1 text-xs font-mono font-semibold rounded-full bg-cyber-success/20 text-cyber-success border border-cyber-success uppercase tracking-wider">
            READY
          </span>
        );
      case 'GENERATING':
        return (
          <span className="px-3 py-1 text-xs font-mono font-semibold rounded-full bg-cyber-neon-blue/20 text-cyber-neon-blue border border-cyber-neon-blue uppercase tracking-wider animate-pulse">
            GENERATING
          </span>
        );
      case 'ERROR':
        return (
          <span className="px-3 py-1 text-xs font-mono font-semibold rounded-full bg-cyber-error/20 text-cyber-error border border-cyber-error uppercase tracking-wider">
            ERROR
          </span>
        );
    }
  };

  const getScoreColor = (score?: number) => {
    if (!score) return 'text-cyber-text-muted';
    if (score >= 80) return 'text-cyber-success';
    if (score >= 60) return 'text-cyber-neon-green';
    return 'text-cyber-error';
  };

  const getScoreGlow = (score?: number) => {
    if (!score) return '';
    if (score >= 80) return 'shadow-neon-green';
    if (score >= 60) return 'shadow-neon-blue';
    return '';
  };

  const handleMarkAsRead = async () => {
    if (onMarkAsRead && !report.isRead) {
      onMarkAsRead(report.id);
    }
  };

  return (
    <div
      className={`cyber-card border-2 transition-all ${
        report.isRead
          ? 'border-cyber-border'
          : 'border-cyber-neon-blue shadow-neon-blue bg-cyber-bg-secondary'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-cyber-border">
        <div className="flex items-center space-x-3 flex-1">
          <div className="text-3xl">📊</div>
          <div className="flex-1">
            <div className="flex items-center space-x-3">
              <h3 className="text-lg font-display font-bold text-cyber-text-primary uppercase tracking-wider">
                {format(new Date(report.reportDate), 'MMMM d, yyyy')}
              </h3>
              {getStatusBadge()}
              {!report.isRead && (
                <span className="px-2 py-1 text-xs font-mono font-semibold rounded-full bg-cyber-neon-pink text-cyber-bg-primary uppercase animate-pulse">
                  NEW
                </span>
              )}
            </div>
            <p className="text-xs text-cyber-text-muted font-mono mt-1">
              &gt; Generated {format(new Date(report.createdAt), 'HH:mm:ss')}
            </p>
          </div>
        </div>
        {report.overallScore !== undefined && (
          <div className={`text-center px-6 py-3 rounded-lg border-2 border-cyber-border ${getScoreGlow(report.overallScore)}`}>
            <div className={`text-4xl font-display font-bold ${getScoreColor(report.overallScore)}`}>
              {report.overallScore}
            </div>
            <div className="text-xs text-cyber-text-muted font-mono uppercase tracking-wider">SCORE</div>
          </div>
        )}
      </div>

      {/* Summary */}
      {report.summary && (
        <div className="mb-4 p-4 bg-cyber-bg-tertiary border border-cyber-border rounded-lg">
          <h4 className="text-xs font-display font-semibold text-cyber-text-muted mb-2 uppercase tracking-wider">
            &gt; Executive Summary
          </h4>
          <p className="text-cyber-text-primary font-mono text-sm whitespace-pre-line leading-relaxed">
            {report.summary}
          </p>
        </div>
      )}

      {/* Expandable Section */}
      {report.status === 'READY' && (
        <div>
          <button
            onClick={() => {
              setExpanded(!expanded);
              if (!expanded && !report.isRead) {
                handleMarkAsRead();
              }
            }}
            className="cyber-button w-full flex items-center justify-center space-x-2"
          >
            <span>{expanded ? '▼' : '▶'}</span>
            <span>{expanded ? 'COLLAPSE REPORT' : 'VIEW FULL REPORT'}</span>
          </button>

          {expanded && (
            <div className="mt-4 space-y-4 animate-slide-in">
              {/* Key Findings */}
              {report.keyFindings && Array.isArray(report.keyFindings) && report.keyFindings.length > 0 && (
                <div className="cyber-card bg-cyber-bg-secondary border border-cyber-neon-blue">
                  <h4 className="font-display font-semibold text-cyber-neon-blue mb-3 uppercase tracking-wider flex items-center space-x-2">
                    <span>🔍</span>
                    <span>&gt; Key Findings</span>
                  </h4>
                  <div className="space-y-2">
                    {report.keyFindings.map((finding: any, index: number) => (
                      <div key={index} className="bg-cyber-bg-tertiary p-3 rounded-lg border border-cyber-border hover:border-cyber-neon-blue transition-colors">
                        {finding.type === 'metric' && (
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-medium text-cyber-text-primary font-mono">
                                {finding.metric}
                              </span>
                              <span className="text-cyber-neon-blue ml-2 font-mono">
                                {finding.value} {finding.unit}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              {finding.trend && (
                                <span
                                  className={`text-sm font-bold ${
                                    finding.trend === 'UP'
                                      ? 'text-cyber-success'
                                      : finding.trend === 'DOWN'
                                      ? 'text-cyber-error'
                                      : 'text-cyber-text-muted'
                                  }`}
                                >
                                  {finding.trend === 'UP' ? '↗' : finding.trend === 'DOWN' ? '↘' : '→'}
                                </span>
                              )}
                              {finding.progress && (
                                <span className="text-xs text-cyber-neon-green font-mono">
                                  {finding.progress}%
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        {finding.type === 'agent' && (
                          <div>
                            <span className="font-medium text-cyber-neon-purple font-mono">
                              {finding.agent}
                            </span>
                            <span className="text-cyber-text-secondary ml-2 font-mono">
                              - {finding.role}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Agent Reports */}
              {report.agentReports && report.agentReports.length > 0 && (
                <div className="cyber-card bg-cyber-bg-secondary border border-cyber-neon-purple">
                  <h4 className="font-display font-semibold text-cyber-neon-purple mb-3 uppercase tracking-wider flex items-center space-x-2">
                    <span>🤖</span>
                    <span>&gt; Agent Insights</span>
                  </h4>
                  <div className="space-y-3">
                    {report.agentReports.map((agentReport) => (
                      <div
                        key={agentReport.id}
                        className="border-l-4 border-cyber-neon-purple pl-4 py-2 bg-cyber-bg-tertiary rounded-r-lg hover:bg-cyber-bg-primary transition-colors"
                      >
                        <div className="flex items-center space-x-3 mb-2">
                          {agentReport.agent.avatar && (
                            <span className="text-2xl">{agentReport.agent.avatar}</span>
                          )}
                          <div className="flex-1">
                            <div className="font-display font-medium text-cyber-text-primary uppercase tracking-wide">
                              {agentReport.agent.name}
                            </div>
                            <div className="text-xs text-cyber-neon-purple font-mono">
                              &gt; {agentReport.agent.role}
                            </div>
                          </div>
                        </div>
                        <p className="text-cyber-text-primary font-mono text-sm whitespace-pre-line leading-relaxed">
                          {agentReport.findings}
                        </p>
                        {agentReport.insights?.demo && (
                          <div className="mt-2 p-2 bg-cyber-neon-green/10 border border-cyber-neon-green rounded">
                            <p className="text-xs text-cyber-neon-green font-mono">
                              💡 Demo mode - Configure ANTHROPIC_API_KEY for full AI insights
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {report.recommendations && Array.isArray(report.recommendations) && report.recommendations.length > 0 && (
                <div className="cyber-card bg-cyber-bg-secondary border border-cyber-neon-green">
                  <h4 className="font-display font-semibold text-cyber-neon-green mb-3 uppercase tracking-wider flex items-center space-x-2">
                    <span>💡</span>
                    <span>&gt; Recommendations</span>
                  </h4>
                  <div className="space-y-2">
                    {report.recommendations.map((rec: any, index: number) => (
                      <div
                        key={index}
                        className={`p-3 rounded-lg border-l-4 ${
                          rec.priority === 'high'
                            ? 'bg-cyber-error/10 border-cyber-error'
                            : rec.priority === 'medium'
                            ? 'bg-cyber-neon-green/10 border-cyber-neon-green'
                            : 'bg-cyber-neon-blue/10 border-cyber-neon-blue'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium font-mono text-sm text-cyber-text-primary">
                            {rec.title}
                          </span>
                          <span
                            className={`text-xs font-display font-bold uppercase tracking-wider ${
                              rec.priority === 'high'
                                ? 'text-cyber-error'
                                : rec.priority === 'medium'
                                ? 'text-cyber-neon-green'
                                : 'text-cyber-neon-blue'
                            }`}
                          >
                            {rec.priority}
                          </span>
                        </div>
                        <p className="text-sm text-cyber-text-secondary font-mono">
                          {rec.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Error State */}
      {report.status === 'ERROR' && (
        <div className="bg-cyber-error/10 border border-cyber-error rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <span className="text-cyber-error text-lg">⚠️</span>
            <p className="text-cyber-error font-mono text-sm flex-1">
              &gt; ERROR: Report generation failed. Please try again.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
